import {useEffect, useRef, useState} from "react";
import { getCountries, isPossiblePhoneNumber } from "react-phone-number-input";
import { PrimaryButton } from "../Buttons";
import { CheckboxInput, PasswordInput, SelectInput, TelephoneInput, TextInput } from "../Inputs";
import {useAdminRegistrationStepStore, useLoaderStore, useRegistrationStore} from "../../context";
import { useAlert, useApi } from "../../hooks";
import { formatCurrency, isValidEmail, isValidPassword } from "../../utils/helpers";
import {getTimezonesForCountry} from "countries-and-timezones";
import {Tooltip} from "react-tooltip";
import {InfoSlideOver} from "../Menus";
import constants from "../../utils/constants";
import { Link, useNavigate } from "react-router-dom";

export default function TermsAndPolicyRegistrationForm(){
    const { setRegistrationUser, registrationUser, registrationAccount, registrationSysTenant, clearAll, setRegistrationAccount } =
    useRegistrationStore();
    const alert = useAlert();
    const {post, fetch} = useApi();
    const navigate = useNavigate();
    const {setShowLoader} = useLoaderStore();
    const [acceptedTermAndConditions, setAcceptedTermAndConditions] = useState(registrationUser?.acceptedTermAndConditions ?? false);
    const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(registrationUser?.privacyPolicyAcceptedAt ?? false);
    const [license, setLicense] = useState();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        async function getDefaultLicense() {
            if (isLoading) return;
            setIsLoading(true);
            fetch('public/GetDefaultLicense')
            .then((lic) => {
                if (lic) setLicense(lic);
            })
            .catch((err) => {
                console.error("Unable to fetch license", err);
                alert("Unable to fetch license", "Please try registering at a different time", "warning");
            })
            .finally(() => {
                setIsLoading(false);
            });
        }
        getDefaultLicense();
    }, []);

    const isValid = () => {
        console.log("acceptedTermAndConditions", acceptedTermAndConditions)
        console.log("acceptedPrivacyPolicy", acceptedPrivacyPolicy)
        if (!acceptedTermAndConditions) {
          alert("Invalid input", "Terms and conditions must be accepted before registering", "warning");
          return false;
        }    
        if (!acceptedPrivacyPolicy) {
          alert("Invalid input", "Privacy policy must be accepted before registering", "warning");
          return false;
        }
        if (!registrationAccount?.paymentMethod?.id) {
            alert("Invalid input", "Please reenter payment information", "warning");
            return false;
        }
        return true;
    };

    async function handleSubmit() {
        if (!isValid()) return;
        let userInfo = {
            ...registrationUser,
            acceptedTermAndConditions,
            acceptedPrivacyPolicy
        };
        setRegistrationUser(userInfo)
        await createAccount(userInfo);
    }

    async function createAccount(userInfo) {
        if (!userInfo) return;
        setShowLoader(true);
        let strippedAccount = {...registrationAccount};
        strippedAccount.paymentMethodId = registrationAccount.paymentMethod.id;
        delete strippedAccount.paymentMethod;
        await post("public/AdminRegister", {
            user: userInfo,
            account: strippedAccount,
            systemTenant: registrationSysTenant,
        })
        .then((res) => {
            alert(
                "Successfully Registered",
                "Check your inbox to confirm your email",
                "success",
                false,
            );
            clearAll();
            navigate("/");
        })
        .catch((err) => {
            // console.log("REG ERROR", err)
            let message = (err.data.success === false && typeof err.data.message === "string")
                ? err.data.message
                : "Unable to register your account";
            alert("Unsuccessful", message, "error");
            setRegistrationAccount({
            ...registrationAccount,
            paymentMethod: null
        });
        })
        .finally(() => {
            setShowLoader(false);
        });
    }

    return (
      <div className="w-10/12 md:w-9/12 space-y-4">
        {!!license && (<div className="flex flex-col justify-start">
            <h2 className="text-primaryColor text-xl font-semibold leading-7 text-gray-900">
                By accepting you will placed on the membership below
            </h2>
            <div className="border-b my-2"/>
            <h3 className="text-base font-semibold leading-7 text-gray-900">
                {license.name}
            </h3>
            <p className="text-sm leading-6 text-gray-500">
            {license.description}
            </p>
            <p className="mt-6 mb-12 flex items-baseline gap-x-1">
            <span className="text-5xl font-bold tracking-tight text-gray-900">
                {license.price === 0
                ? "Free"
                : formatCurrency(license.price, true)}
            </span>
            {license.price > 0 && (
                <span className="text-sm font-semibold leading-6 text-gray-600">
                /{constants.DURATION_TYPES.find(x => x.label.toLowerCase() === license.durationType.toLowerCase())?.singular.toLowerCase()}
                </span>
            )}
            </p>
        </div>)}
        <div className="text-primaryColor text-lg font-semibold flex flex-row justify-start border-b">
          Terms and Policy
        </div>
        <div className="flex flex-col justify-between mt-4">
            <CheckboxInput
                textSize="text-sm"
                label={
                    <div>
                        I accept {constants.APP_NAME}'s &nbsp;
                        <span className="text-primary cursor-pointer underline">
                            <Link
                                to="/termsAndConditions"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Terms & Conditions
                            </Link>
                        </span>
                    </div>
                }
                name="t&c"
                checked={acceptedTermAndConditions}
                onChange={() =>
                    setAcceptedTermAndConditions(!acceptedTermAndConditions)
                }
            />
            <CheckboxInput
                textSize="text-sm"
                label={
                    <div>
                        I accept {constants.APP_NAME}'s &nbsp;
                        <span className="text-primary cursor-pointer underline">
                            <Link
                                to="/privacyPolicy"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Privacy Policy
                            </Link>
                        </span>
                    </div>
                }
                name="t&c"
                checked={acceptedPrivacyPolicy}
                onChange={() =>
                    setAcceptedPrivacyPolicy(!acceptedPrivacyPolicy)
                }
            />
        </div>
        <PrimaryButton
          text="SUBMIT"
          onClick={handleSubmit}
        />
      </div>
    );
}